import styles from './DevExpressLogoFileController.module.scss';

import { Delete, UploadFile } from '@mui/icons-material';
import { IconButton, LinearProgress, Stack } from '@mui/material';
import { ChangeEventHandler, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useDialogs } from '@work4all/components';
import { Divider } from '@work4all/components/lib/dataDisplay/divider/Divider';
import { BaseActionButton } from '@work4all/components/lib/input/base-action-button/BaseActionButton';
import { NumberInput } from '@work4all/components/lib/input/number-input';

import { useSecureBlobUrl } from '@work4all/data';
import { useUploadTempFile } from '@work4all/data/lib/hooks/use-upload-temp-file';

import { useDevExpressReportSettings } from './hooks/use-dev-express-report-settings';

const EMPTY_UUID = '00000000-0000-0000-0000-000000000000';

export const DevExpressLogoFileController = (props: {
  logoType: 'logo1' | 'logo2';
  onChange?: () => void;
}) => {
  const { logoType, onChange } = props;

  const [uploadingLogo, setUploadingLogo] = useState<boolean>(false);

  const uploadTempFile = useUploadTempFile();

  const {
    res: { refetch, data },
    mutate,
  } = useDevExpressReportSettings();

  const logoBlob = useSecureBlobUrl(data[logoType + 'Url']);
  const dialogs = useDialogs();

  const { t } = useTranslation();

  const handlePosChange = useCallback(
    async (mode: 'PosX' | 'PosY', pos: number) => {
      if (pos !== data[logoType + mode]) {
        await mutate({
          variables: {
            [logoType + mode]: pos,
          },
        });
        await refetch();
        onChange?.();
      }
    },
    [data, logoType, mutate, refetch, onChange]
  );

  const handleFileChange = useCallback<ChangeEventHandler<HTMLInputElement>>(
    async (e) => {
      setUploadingLogo(true);
      const file = e.currentTarget.files?.[0];
      if (file) {
        const tempFile = await uploadTempFile(file);
        if (tempFile) {
          await mutate({
            variables: {
              [logoType]: tempFile.generatedObject,
            },
          });
          await logoBlob.mutate();
          await refetch();
          onChange?.();
        }
      }

      setUploadingLogo(false);
    },
    [uploadTempFile, mutate, logoType, logoBlob, refetch, onChange]
  );

  const handleDelete = useCallback(async () => {
    dialogs.confirm({
      title: t('DEVEXPRESS_REPORT.DELETE_LOGO'),
      description: t('DEVEXPRESS_REPORT.DELETE_LOGO_DESCRIPTION'),
      onConfirm: async () => {
        await mutate({
          variables: {
            [logoType]: EMPTY_UUID,
          },
        });
        await refetch();
        onChange?.();
      },
    });
  }, [dialogs, logoType, mutate, onChange, refetch, t]);

  return (
    <Stack gap="0.5rem">
      <Stack direction="row" gap="0.5rem" alignItems="center" width="100%">
        <Divider
          title={t(`DEVEXPRESS_REPORT.${logoType}`)}
          style={{ flex: '1', padding: '0' }}
        />
        {logoBlob?.blobUrl && (
          <IconButton onClick={handleDelete} className={styles.deleteButton}>
            <Delete />
          </IconButton>
        )}
      </Stack>

      <div className={styles.wrap}>
        {uploadingLogo && (
          <LinearProgress
            style={{
              width: '100%',
              position: 'absolute',
            }}
          />
        )}
        {logoBlob?.blobUrl ? (
          <img
            src={logoBlob?.blobUrl}
            className={styles.img}
            alt="Company Logo"
          />
        ) : (
          <BaseActionButton icon={<UploadFile />}>
            {t('DEVEXPRESS_REPORT.UPLOAD')}
          </BaseActionButton>
        )}
        <input
          type="file"
          name="logo"
          multiple={false}
          onChange={handleFileChange}
          className={styles.input}
        />
      </div>
      {logoBlob?.blobUrl && (
        <Stack direction="row" gap="1rem">
          <NumberInput
            label={t('DEVEXPRESS_REPORT.POS_X')}
            value={data[logoType + 'PosX']}
            unit="mm"
            onBlur={(e) => handlePosChange('PosX', e)}
            selectOnFocus
          />
          <NumberInput
            label={t('DEVEXPRESS_REPORT.POS_Y')}
            value={data[logoType + 'PosY']}
            unit="mm"
            onBlur={(e) => handlePosChange('PosY', e)}
            selectOnFocus
          />
        </Stack>
      )}
    </Stack>
  );
};
